import { NotificationBar, TYPE_ERROR } from '@eventbrite/eds-notification';
import { cardTypeForNumber } from '@eventbrite/eds-utils';
import classnames from 'classnames';
import noop from 'lodash/noop';
import React, { FunctionComponent, useState } from 'react';
import { checkCardImprovementsFeatureGate } from '../../utils/statsig';
import { CardExpirationDateField } from './cardExpirationDateField';
import { CardSecurityCodeField } from './CardSecurityCodeField';
import { BIN_LENGTH } from './constants';
import { CreditCardField } from './CreditCardField';
import './paymentInfoFields.scss';
import { PostalCodeField } from './PostalCodeField';
import type { ShouldDisplayError } from './types';

const TopLevelError: FunctionComponent<{ error?: React.ReactNode }> = ({
    error,
}) =>
    error ? (
        <div className="eds-g-cell eds-g-cell-1-1 eds-l-mar-bot-3">
            <NotificationBar type={TYPE_ERROR} hasCloseButton={true}>
                <span>{error}</span>
            </NotificationBar>
        </div>
    ) : null;

interface PaymentInfoFieldsProps {
    /**
     * Error message to display above the payment info fields.
     * ex. 'Please enter a valid credit card number.'
     */
    error?: React.ReactNode;
    /**
     * A callback triggered when the value of input changes, passing in the value and name of the field changes
     * ex. (value, name) => { console.log(value, name) // '05/99', 'cardExpirationDate' }
     */
    onChange?: (value: string, name: string) => void;
    /**
     * A callback triggered when the input is blurred.
     */
    onBlur?: (value: string, name: string) => void;
    /**
     * A callback triggered when the credit card type changes
     */
    onCardTypeChange?: (value: string | null) => void;
    /**
     * A callback triggered when the bin changes (first six digits of CC)
     */
    onBinChange?: (value: string) => void;
    /**
     * A function passed to ValidationFormField to determine if errors should be shown
     */
    shouldDisplayError?: ShouldDisplayError;
    /**
     * Whether to show asterisk to indicate field is required
     */
    shouldDisplayRequired?: boolean;
    /**
     * Whether to show the Postal Code field that may not be required for a given country
     */
    shouldDisplayPostalCode?: boolean;
    /**
     * OnCardTypeChange fires back to CreditPaymentFields which determines whether there is a
     * paymentConstraint error. This can't be in the validators because it needs access to state
     */
    constrainedPaymentMethodError?: boolean;
    /**
     * Content to be displayed as annotation below the input
     * element when there is a payment constraint error.
     */
    paymentConstraintAnnotationNote?: React.ReactNode;
}

export const PaymentInfoFields: FunctionComponent<PaymentInfoFieldsProps> = ({
    error,
    shouldDisplayRequired,
    shouldDisplayPostalCode = true,
    constrainedPaymentMethodError,
    paymentConstraintAnnotationNote,
    shouldDisplayError,
    onBinChange = noop,
    onCardTypeChange = noop,
    onChange = noop,
    onBlur = noop,
}) => {
    const [cardType, setCardType] = useState<string | null>(null);

    const _handleChange = (name: string, value: string) =>
        onChange(value, name);

    const _handleBlur = (name: string, value: string) => onBlur(value, name);

    const _handleCCNumberChange = (value: string) => {
        const cardTypeMatch: string = cardTypeForNumber(value);
        const cardType: string | null = cardTypeMatch ? cardTypeMatch : null;

        const binNumber =
            value.length >= BIN_LENGTH ? value.substr(0, BIN_LENGTH) : '';

        onBinChange(binNumber);
        onCardTypeChange(cardType);
        setCardType(cardType);
    };

    const isCardFormRevampEnabled = checkCardImprovementsFeatureGate();

    return (
        <div className="eds-payment-info-fields eds-g-group">
            <main className="eds-payment-info-input-fields-container eds-l-pad-bot-2">
                <TopLevelError error={error} />
                <div
                    className="eds-g-cell eds-g-cell-12-12 eds-l-pad-bot-2"
                    data-spec="eds-credit-card-field"
                >
                    <CreditCardField
                        shouldDisplayError={shouldDisplayError}
                        shouldDisplayRequired={shouldDisplayRequired}
                        cardType={cardType}
                        onChange={_handleCCNumberChange}
                        onBlur={_handleBlur}
                        constrainedPaymentMethodError={
                            constrainedPaymentMethodError
                        }
                        paymentConstraintAnnotationNote={
                            paymentConstraintAnnotationNote
                        }
                    />
                </div>
                <div
                    className={classnames(`eds-g-cell`, {
                        'eds-g-cell-5-12 eds-l-pad-right-2':
                            shouldDisplayPostalCode,
                        'eds-g-cell-8-12 eds-l-pad-right-2':
                            !shouldDisplayPostalCode,
                        'checkout-expiration-date-field':
                            isCardFormRevampEnabled,
                    })}
                    data-spec="eds-card-expiration-date-field"
                >
                    <CardExpirationDateField
                        shouldDisplayError={shouldDisplayError}
                        shouldDisplayRequired={shouldDisplayRequired}
                        // onBlur event is intentionally ignored to prevent inifinite loops when Chrome autofills a credit card!
                    />
                </div>
                <div
                    className={classnames(`eds-g-cell`, {
                        'eds-g-cell-3-12 eds-l-pad-hor-2':
                            shouldDisplayPostalCode,
                        'eds-g-cell-4-12 eds-l-pad-left-2':
                            !shouldDisplayPostalCode,
                        'checkout-security-code-field': isCardFormRevampEnabled,
                    })}
                    data-spec="eds-card-security-code-field"
                >
                    <CardSecurityCodeField
                        shouldDisplayError={shouldDisplayError}
                        shouldDisplayRequired={shouldDisplayRequired}
                        cardType={cardType}
                        onChange={_handleChange}
                        onBlur={_handleBlur}
                    />
                </div>
                {shouldDisplayPostalCode && (
                    <div
                        className="eds-g-cell eds-g-cell-4-12 eds-l-pad-left-2"
                        data-spec="eds-card-postal-code-field"
                    >
                        <PostalCodeField
                            shouldDisplayError={shouldDisplayError}
                            shouldDisplayRequired={shouldDisplayRequired}
                            onChange={_handleChange}
                            onBlur={_handleBlur}
                        />
                    </div>
                )}
            </main>
        </div>
    );
};
